<script setup lang="ts">
import { computed } from "vue";

import { VueECharts as VChart } from "@Helpers/echarts";
import useTranslation from "@Resources/js/composables/useTranslation.ts";
import TopReferrerStats from "@Modules/analytics/DTOs/TopReferrerStats";
import ChartLayout from "@Modules/analytics/Components/ChartLayout.vue";

import { ValueChartDataProvider } from "./ValueChart";

//
// Props
interface Props {
  title?: string | null;
  dataset?: ValueChartDataProvider;
  loading?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  title: "Top Referrer",
  dataset: () => new TopReferrerStats(),
  loading: false,
});

//
// Setup
const { __ } = useTranslation();

//
// Computed
const chartOptions = computed(() => {
    return {
        title: {
            text: __("chart.title.top_referrer"),
            left: "center",
            top: "top",
            show: false,
        },
        toolbox: {
            show: true,
            feature: {
                saveAsImage: { show: true, title: __("chart.save_as_image") },
            },
        },
        grid: {
            left: "15",
            top: "0",
            right: "15",
            bottom: "24",
        },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "none",
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- echarts v4 formatter callback params are untyped
            formatter: (params: any) => {
                const total = props.dataset.getChartTotal();
                const percent =
                    Math.round(
                        ((params[0].data.value * 100) / (total === 0 ? 1 : total) || 0) * 100,
                    ) / 100;
                const value = parseInt(params[0].data.value).toLocaleString();

                return (
                    params[0].name +
                    "<br/>" +
                    __("chart.clicks") +
                    `: ${value} (${percent} %)`
                );
            },
        },
        xAxis: [
            {
                inverse: true,
                type: "value",
                max: props.dataset.getChartTotal(),
                axisLine: {
                    show: false,
                },
                splitLine: false,
                axisLabel: {
                    fontWeight: "bold",
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- echarts v4 formatter callback params are untyped
                    formatter: (value: any) => {
                        const total = props.dataset.getChartTotal();
                        if (total === 0) {
                            return 100;
                        }
                        let percent = (value * 100) / total;
                        percent = percent || 0;
                        if (percent > 100) return "";
                        return Math.round((percent * 100) / 100).toLocaleString() + "%";
                    },
                },
            },
        ],
        yAxis: [
            {
                type: "category",
                data: props.dataset.getChartLabels(),
                splitLine: {
                    show: true,
                },
                axisTick: {
                    show: false,
                },
                axisLabel: {
                    show: true,
                    inside: true,
                    fontWeight: "bold",
                },
                axisLine: {
                    show: false,
                },
            },
        ],
        series: [
            {
                id: "source_clicks",
                type: "bar",
                barCategoryGap: "0%",
                name: __("chart.clicks"),
                itemStyle: {
                    normal: {
                        color: "rgba(51, 152, 219, 0.2)",
                    },
                    emphasis: {
                        color: "rgba(51, 152, 219, 0.4)",
                    },
                },
                label: {
                    normal: {
                        show: true,
                        color: "rgb(51, 138, 207)",
                        fontWeight: "bold",
                        position: "insideRight",
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- echarts v4 formatter callback params are untyped
                        formatter: (params: any) => {
                            let percent = (params.value / props.dataset.getChartTotal()) * 100;
                            percent = percent || 0;
                            return (
                                (Math.round(percent * 100) / 100).toLocaleString() + "%"
                            );
                        },
                        fontSize: 14,
                    },
                },
                data: props.dataset.getChartData(),
            },
        ],
    };
});
</script>

<template>
  <ChartLayout
    class="percent-value"
    :is-loading="loading"
  >
    <template #header>
      {{ props.title }}
    </template>

    <v-chart
      ref="chart"
      class="percent-value__chart"
      :options="chartOptions"
    />
  </ChartLayout>
</template>

<style lang="scss" scoped>
.percent-value {
  &__chart {
    width: 100%;
    min-width: 0;
    height: 200px;
  }
}
</style>
